import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MobileUserTable from "../../components/tables/MobileUserTable";
import { api } from "../../utils/apiClient";
import LoadingShimmer from "../../utils/LoadingShimmer";
import GlobalFilter from "../../components/filters/GlobalFilter";
import { Icon } from "@iconify/react/dist/iconify.js";

let searchTimeout;

const MobileUserPage = () => {
  const [mobileUsers, setMobileUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);

  //pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  //sort
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  //search
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [refreshTick, setRefreshTick] = useState(0);
  const navigate = useNavigate();
  const [nearByFilter, setNearByFilter] = useState(null);
  const [nearbyToggle, setNearbyToggle] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [langFilter, setLangFilter] = useState("all");

  useEffect(() => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 1000);
    return () => clearTimeout(searchTimeout);
  }, [search]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);

        let lat = null;
        let lng = null;

        if (nearbyToggle) {
          try {
            const location = await getUserLocation();
            lat = location.lat;
            lng = location.lng;
            setLocationError(false);
            console.log("User location obtained:", lat, lng);
          } catch (err) {
            setLocationError(true);
            console.error("Location permission denied or error:", err);
          }
        }

        const response = await api.get("/mobile-users", {
          params: {
            page,
            limit: entriesPerPage,
            sort,
            order,
            search: debouncedSearch?.trim() || "",
            status: statusFilter?.value ?? "",
            nearBy: nearbyToggle ? "yes" : "",
            lat,
            lng,
            app_name: langFilter !== "all" ? langFilter : undefined,
          }
        });
        setMobileUsers(response.data.data || []);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalEntries(response.data.pagination.total || 0);
      } catch (error) {
        console.error("Error fetching mobileUsers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [page, entriesPerPage, sort, order, debouncedSearch, statusFilter, nearByFilter, nearbyToggle, langFilter, refreshTick]);

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Geolocation not supported");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        (err) => reject(err)
      );
    });
  };

  const statusOptions = [
    { id: 1, value: true, title: "Active" },
    { id: 2, value: false, title: "Inactive" },
  ];



  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/roles");
        setRoles(res.data?.data || []);
      } catch (error) {
        console.error(error);
      }
    };
    fetchRoles();
  }, []);

  const handleUserDeleted = (id) => {
    setMobileUsers((prev) => prev.filter((user) => user.id !== id));
  };

  const handleEditUser = (user) => {
    navigate(`/edit-user/${user.id}`, { state: roles });
  };

  const handleApplyFilters = (filters) => {
    const status = filters?.status || null; // get selectedStatus object
    setSelectedStatus(status);
    setStatusFilter(status);
    if (filters?.nearBy !== undefined) {
      setNearByFilter(
        nearByUsersYesOrNo.find(opt => opt.value === filters.nearBy) || null
      );
    }
    setPage(1);
  };

  const handleClearFilters = () => {
    setSelectedStatus(null);
    setStatusFilter("");
    setNearByFilter(null);
    setPage(1);
  };

  const nearByUsersYesOrNo = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" }
  ]

  return (
    <>
      <div className="page-header-row">
        <Breadcrumb title="Users Management" />
        <div className="filter-wrapper">
          <div className="d-flex align-items-center gap-3">
            <Icon
              icon="mdi:refresh"
              width="26"
              height="26"
              className={`ms-2 d-inline-flex d-md-none mobile-refresh-icon ${loading ? "spin" : ""}`}
              onClick={() => setRefreshTick(t => t + 1)}
            />
            <div className="filter-btn-wrapper">
              <button
                className="filter-btn"
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              >
                <img src="assets\images\filter.png" alt="" className="filter-icon" />
              </button>
            </div>
          </div>
        </div>

      </div>
      {/* Filter panel (toggle on mobile, always visible on desktop) */}
      <div className={`filter-panel ${mobileFilterOpen ? "open" : ""}`}>
        <GlobalFilter
          showNearByUsers={false}
          nearByOptions={nearByUsersYesOrNo}
          selectedNearBy={nearByFilter}
          onNearByChange={(val) => {
            setNearByFilter(val);
          }}
          onSearch={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </div>

      {loading ? (
        < LoadingShimmer
          columns={8}
          rows={6}
          avatarColumnIndex={2}
          actionColumnIndex={7}
        />
      ) : (
        <MobileUserTable
          mobileUsers={mobileUsers}
          roles={roles}
          onUserDeleted={handleUserDeleted}
          onEdit={handleEditUser}
          nearbyToggle={nearbyToggle}
          setNearbyToggle={setNearbyToggle}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalEntries={totalEntries}
          sort={sort}
          order={order}
          setSort={setSort}
          setOrder={setOrder}
          search={search}
          setSearch={setSearch}
          entriesPerPage={entriesPerPage}
          setEntriesPerPage={setEntriesPerPage}
          locationError={locationError}
          langFilter={langFilter}
          setLangFilter={setLangFilter}
          onRefresh={() => setRefreshTick(t => t + 1)}
        />
      )}
    </>
  );
};

export default MobileUserPage;